import { useQuery, useQueryClient } from "@tanstack/react-query"
import { fetcher } from "../fetcher"
import { retrieveValue, storeValue } from "../../utils"
import { usePostHog } from "../../hooks"
import { PostHog } from "posthog-react-native"

type Response = {
  status: string
  referenceId: string
  redirectUrl?: string
}

type HookParams = {
  verificationId?: string
  code?: string
  enabled?: boolean
  /** When true, leave error UI to the phone-auth machine (no GeneralError nav). */
  disableErrorScreen?: boolean
  posthog: PostHog
}

function getQueryOptions({
  verificationId = "",
  code = "",
  enabled,
  disableErrorScreen,
  posthog,
}: HookParams) {
  return {
    queryKey: ["accessToken"],
    queryFn: async () => {
      if ((await retrieveValue("mode")) === "demo") {
        await storeValue("refreshToken", "demo-token")
      }
      const queryString = new URLSearchParams({ verificationId, code }).toString()
      const response = await fetcher(
        `/Verification/CheckVerification?${queryString}`,
        {
          method: "GET",
        },
        { disableErrorScreen }
      )
      const responseJson = await response.json()

      posthog.identify(responseJson.referenceId)

      return responseJson
    },
    enabled,
    refetchOnWindowFocus: false,
  }
}

export function useFetchCheckPhoneVerification() {
  const queryClient = useQueryClient()
  const posthog = usePostHog()

  return (params: Omit<HookParams, "posthog">) =>
    queryClient.fetchQuery<Response>(
      getQueryOptions({
        // Read when the check runs: VerifyPhoneNumber writes the id long after
        // this hook first rendered, and callers may hold on to this function.
        verificationId: queryClient.getQueryData<string>(["phoneNumberVerificationId"]),
        ...params,
        posthog,
      })
    )
}

export function useCheckPhoneVerification(params: Omit<HookParams, "posthog">) {
  const queryClient = useQueryClient()
  const verificationId = queryClient.getQueryData<string>(["phoneNumberVerificationId"])
  const posthog = usePostHog()

  return useQuery<Response>(getQueryOptions({ ...params, verificationId, posthog }))
}
